import React from 'react';
import style from 'acscp/components/flightRequest/form/flightRequestForm.module.scss';
import classNames from 'classnames';
import { FetchRequest } from 'acscp/actions';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { Dispatch } from 'redux';
import { AppState } from 'acscp/store';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { CreateFlightRequestRequest, EditFlightRequestRequest, RequestClass } from 'acscp/api/requests';
import { FlightRequest } from 'acscp/api/models/flightRequest';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import {
    FlightRequestFormLeg,
    FormFlightRequest,
    mapBookingToFormFlightRequest,
    mapFormFlightRequestToFlightRequest
} from 'acscp/components/flightRequest/form/helpers/flightRequestFormMapper';
import { FlightRequestLegsForm } from 'acscp/components/flightRequest/form/flightRequestLegsForm';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { Booking } from 'acscp/store';
import { CancelFlightRequestRequest } from 'acscp/api/requests/cancelFlightRequestRequest';
import { updateFlightRequestWithTripType } from './helpers/tripTypeHelper';
import { defaultLeg } from './helpers/defaultLeg';
import { FlightRequestConfirmationDialog } from './flightRequestConfirmationDialog';
import { Customer } from 'acscp/api/models/customer';
import { TripType } from './helpers/tripType';
import { Row } from 'acscp/components/common/row';

export const tripTypeTranslations = {
    [TripType.OneWay]: defineMessages({
        tripType: {
            id: 'components.flightRequest.form.flightRequestForm.oneWay',
            defaultMessage: 'ONE WAY'
        }
    }),
    [TripType.Return]: defineMessages({
        tripType: {
            id: 'components.flightRequest.form.flightRequestForm.return',
            defaultMessage: 'RETURN'
        }
    }),
    [TripType.MultiLeg]: defineMessages({
        tripType: {
            id: 'components.flightRequest.form.flightRequestForm.multiLeg',
            defaultMessage: 'MULTI-LEG'
        }
    }),
};

interface FlightRequestFormOwnProps {
    booking?: Booking;
    isRepeatRequest: boolean;
    onModified: () => void;
}

type FlightRequestFormStateProps = RequestStateProps & {
    isSubmitLoading: boolean;
    isCancelLoading: boolean;
    customers: Customer[];
    hasAccountManager: boolean;
};

interface FlightRequestFormDispatchProps {
    createFlightRequest: (flightRequest: FlightRequest) => void;
    editFlightRequest: (flightRequest: FlightRequest, bookingId: string) => void;
    cancelFlightRequest: (bookingId: string) => void;
}

type FlightRequestFormProps = FlightRequestFormOwnProps
    & FlightRequestFormStateProps
    & FlightRequestFormDispatchProps;

interface FlightRequestFormState {
    flightRequest: FormFlightRequest;
    isCancelConfirmationOpen: boolean;
}

class FlightRequestFormPresentational
    extends  PresentationComponentBase<FlightRequestFormOwnProps & InjectedIntlProps,
                                       FlightRequestFormStateProps,
                                       FlightRequestFormDispatchProps,
                                       FlightRequestFormState> {
    private validateForm: () => boolean = () => true;

    constructor(props: FlightRequestFormProps & InjectedIntlProps) {
        super(props);
        this.state = {
            flightRequest: props.booking
                ? mapBookingToFormFlightRequest(props.booking, props.isRepeatRequest)
                : {
                    tripType: TripType.OneWay,
                    legs: [defaultLeg()],
                    customerId: '',
                    originalBookingFriendlyId: null
                },
            isCancelConfirmationOpen: false
        };
    }

    componentDidUpdate(): void {
        const { customers } = this.props;
        const currentCustomerId = this.state.flightRequest.customerId;

        const mustUpdateCustomerSelection =
            (!currentCustomerId && customers.length > 0) ||
            (currentCustomerId && customers.map(c => c.id).indexOf(currentCustomerId) === -1);

        if (mustUpdateCustomerSelection) {
            const flightRequest = {
                ...this.state.flightRequest,
                customerId: customers.length > 0 ? customers[0].id : ''
            };
            this.setState({flightRequest});
            this.props.onModified();
        }
    }

    handleLegsChange = (legs: FlightRequestFormLeg[]) => {
        let flightRequest = {
            ...this.state.flightRequest,
            legs
        };

        if (flightRequest.tripType === TripType.Return && legs.length > 2) {
            flightRequest = updateFlightRequestWithTripType(flightRequest, TripType.MultiLeg);
        }

        this.setState({flightRequest});
        this.props.onModified();
    }

    handleCustomerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const flightRequest = {
            ...this.state.flightRequest,
            customerId: event.target.value
        };
        this.setState({flightRequest});
        this.props.onModified();
    }

    handleTripTypeChange = (tripType: TripType) => {
        const flightRequest = updateFlightRequestWithTripType(this.state.flightRequest, tripType);
        this.setState({flightRequest});
        this.props.onModified();
    }

    submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (this.validateForm()) {
            this.isNewFlightRequest(this.props.booking)
                ? this.props.createFlightRequest(mapFormFlightRequestToFlightRequest(this.state.flightRequest))
                : this.props.editFlightRequest(mapFormFlightRequestToFlightRequest(this.state.flightRequest), this.props.booking.id);
        }

        const updatedLegs = this.state.flightRequest.legs.map(l => {
            return {...l, showValidationErrors: true};
        });
        this.handleLegsChange(updatedLegs);
    }

    openCancelConfirmation = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        this.setState({isCancelConfirmationOpen: true});
    }

    closeCancelConfirmation = () => {
        this.setState({isCancelConfirmationOpen: false});
    }

    cancel = () => {
        this.closeCancelConfirmation();
        if (!this.isNewFlightRequest(this.props.booking)) {
            this.props.cancelFlightRequest(this.props.booking.id);
        }
    }

    renderTripTypeSelector = () => {
        const {intl} = this.props;
        const {flightRequest} = this.state;

        return (
            <div className={style.TripTypes}>
                {Object.keys(TripType).map((tt: keyof typeof TripType) => {
                    const tripType = TripType[tt];
                    return (
                        <label
                            className={flightRequest.tripType === tripType ? style.SelectedTripType : ''}
                            key={tripType}
                        >
                            {intl.formatMessage(tripTypeTranslations[tripType].tripType)}
                            <input
                                type="radio"
                                value={tripType}
                                checked={flightRequest.tripType === tripType}
                                onChange={() => this.handleTripTypeChange(tripType)}
                            />
                        </label>
                    );
                })}
            </div>
        );
    }

    render(): JSX.Element {
        const {isSubmitLoading, isCancelLoading, booking, customers, hasAccountManager} = this.props;
        const {flightRequest, isCancelConfirmationOpen} = this.state;
        const customerOptions = customers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
        ));

        return (
            <Row>
                <div className={style.UsageInstruction}>
                    {hasAccountManager ? (
                        <FormattedMessage
                            id={'components.pages.flightRequest.usageInstruction'}
                            defaultMessage={'Tell us your requirements and your account manager will provide you with' +
                            ' a no obligation quote as soon as possible.'}
                        />
                    ) : (
                        <FormattedMessage
                            id={'components.pages.flightRequest.usageInstructionWithoutAccountManager'}
                            defaultMessage={'Tell us your requirements and one of our Account Managers will provide you' +
                            ' with a no obligation quote as soon as possible.'}
                        />
                    )}
                </div>
                <form
                    className={style.Form}
                    noValidate={true}
                    onSubmit={this.submit}
                >
                    {customers.length > 1 &&
                        <div className={style.AccountSelectionContainer}>
                            <label>
                                <FormattedMessage
                                    id={'components.flightRequest.form.flightRequestForm.account'}
                                    defaultMessage={'ACCOUNT'}
                                />
                                <select
                                    className={classNames(style.Input, style.AccountSelection)}
                                    name="account"
                                    id="account"
                                    value={flightRequest.customerId}
                                    onChange={this.handleCustomerChange}
                                >
                                    {customerOptions}
                                </select>
                            </label>
                        </div>
                    }
                    {this.renderTripTypeSelector()}
                    <FlightRequestLegsForm
                        legs={flightRequest.legs}
                        onChange={this.handleLegsChange}
                        showAddAndRemoveButtons={flightRequest.tripType === TripType.MultiLeg || flightRequest.tripType === TripType.Return}
                        enquiryButtons={[
                            <div className={style.ButtonWrapper} key="submit">
                                <ButtonWithLoadingIndicator
                                    className={classNames(style.Button, style.EnquireButton)}
                                    isLoading={isSubmitLoading}
                                >
                                    {this.isNewFlightRequest(booking)
                                        ?
                                        <FormattedMessage
                                            id={'components.flightRequest.form.flightRequestForm.enquireNow'}
                                            defaultMessage={'ENQUIRE NOW'}
                                        />
                                        :
                                        <FormattedMessage
                                            id={'components.flightRequest.form.flightRequestForm.update'}
                                            defaultMessage={'UPDATE'}
                                        />
                                    }
                                </ButtonWithLoadingIndicator>
                            </div>,
                            this.isNewFlightRequest(booking)
                                ? null
                                :
                                <div className={style.ButtonWrapper} key="cancel">
                                    <ButtonWithLoadingIndicator
                                        className={classNames(style.Button, style.CancelButton)}
                                        isLoading={isCancelLoading}
                                        onClick={this.openCancelConfirmation}
                                    >
                                        <FormattedMessage
                                            id={'components.flightRequest.form.flightRequestForm.cancelRequest'}
                                            defaultMessage={'CANCEL REQUEST'}
                                        />
                                    </ButtonWithLoadingIndicator>
                                    <FlightRequestConfirmationDialog
                                        isOpen={isCancelConfirmationOpen}
                                        closeDialog={this.closeCancelConfirmation}
                                        action={this.cancel}
                                    />
                                </div>
                        ]}
                        registerValidator={(validateLegs: () => boolean) => this.validateForm = validateLegs}
                    />
                </form>
            </Row>
        );
    }

    private isNewFlightRequest(booking: Booking | undefined): booking is undefined {
        return !this.props.booking || this.props.isRepeatRequest;
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): FlightRequestFormStateProps {
    const createRequestError = getRequestError(CreateFlightRequestRequest);
    const editRequestError = getRequestError(EditFlightRequestRequest);
    const cancelRequestError = getRequestError(CancelFlightRequestRequest);
    return {
        isLoading: createRequestError.isLoading || editRequestError.isLoading || cancelRequestError.isLoading,
        isSubmitLoading: createRequestError.isLoading || editRequestError.isLoading,
        isCancelLoading: cancelRequestError.isLoading,
        success: createRequestError.success && editRequestError.success && cancelRequestError.success,
        customers: state.customerContact.customer ? state.customerContact.customer.customers : [],
        hasAccountManager: !!state.broker.broker
    };
}

function mapDispatchToProps(dispatch: Dispatch): FlightRequestFormDispatchProps {
    return {
        createFlightRequest: (flightRequest: FlightRequest) => dispatch(new FetchRequest(new CreateFlightRequestRequest(flightRequest))),
        editFlightRequest: (flightRequest: FlightRequest, bookingId: string) =>
            dispatch(new FetchRequest(new EditFlightRequestRequest(flightRequest, bookingId))),
        cancelFlightRequest: (bookingId: string) => dispatch(new FetchRequest(new CancelFlightRequestRequest(bookingId)))
    };
}

export const FlightRequestForm = connectWithRequests(mapStateToProps, mapDispatchToProps)
    (injectIntl(FlightRequestFormPresentational));
